import {Grid, Table, TableBody, TableContainer} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import _ from 'lodash';
import moment from 'moment';
import {ecdsMapObservations} from './ecds_data';
import {mapObservationFromAPI} from '../observation/obsUtils';
import config from '../../../../app/config';
import EcdsObsRow from './ecdsObsRow';
import {useStylesECDS} from './ecdsStyle';
import {useSelector} from 'react-redux';
import {codedObservations} from '../../../../store/reducers/dicts';
import InformationBox from '../../../../components/other/informationBox';
import EcdsTableHeader from './components/ecdsTableHeader';
import {getTableScrollbar} from '../../../../util/scrollbar';

const CodedClinicalObservations = (props) => {
    const {t} = useTranslation();
    const classes = useStylesECDS();
    const {version} = props;

    // Table Data
    const [observation, setObservation] = useState(_.isEmpty(props.obs) ? [] : props.obs);
    const [clinicalObs, setClinicalObs] = useState([]);

    // ECDS Data Set
    const [ecds, setEcds] = useState([]);

    // Dictionary for mapping SnomedCode
    const dictionary = useSelector(codedObservations);

    useEffect(() => {
        setObservation(props.obs);
        setClinicalObs([]);
        setEcds([]);
        if (!_.isEmpty(props.obs)) {
            _.forEach(props.obs, obs => {
                const mapped = mapObservationFromAPI(obs);

                addToTable(mapped);
            });
        }
    }, [props.obs]);

    useEffect(() => {
        props.setData(ecds);
    }, [ecds]);

    // Get value form Obs Object
    const getValue = (item, field) => {
        if (item.status === 'completed') {
            // eslint-disable-next-line default-case
            switch (field) {
                case 'respiration':
                    return item?.breaths ? item?.breaths : '-';
                case 'spo2scale':
                    return item?.spo2scale ? item?.spo2scale : '-';
                case 'bloodPressure':
                    return item?.systolicPressure ? item?.systolicPressure : '-';
                case 'pulseRate':
                    return item?.pulseRate ? item?.pulseRate : '-';
                case 'consciousness':
                    return item?.consciousnessLevel?.description ? item?.consciousnessLevel?.description : '-';
                case 'temperature':
                    return item?.temperature ? item?.temperature : '-';
            }
        }
        return '-';
    };

    const createRow = (item, title, field, unit) => {
        if (item?.status === 'completed') {
            return {
                field: field,
                name: title,
                value: getValue(item, field),
                unit: unit,
                timestamp: item?.acceptDate,
            };
        }
    };

    // Transform data to Table
    const addToTable = (obs) => {
        const respRate = createRow(obs.respiration, 'Respiratory Rate', 'respiration', 'Breath/Min');
        const saturation = createRow(obs.spo2scale, 'Inspired O₂%', 'spo2scale', '% Sat');
        const bloodPressure = createRow(obs.bloodPressure, 'Blood Pressure', 'bloodPressure', 'mm[Hg]');
        const pulseRate = createRow(obs.pulseRate, 'Heart Rate', 'pulseRate', 'BPM');
        const consciousness = createRow(obs.consciousness, 'Consciousness Level', 'consciousness', '-');
        const temp = createRow(obs.temperature, 'Temperature', 'temperature', '°C');

        let tests = [
            respRate,
            saturation,
            bloodPressure,
            pulseRate,
            consciousness,
            temp,
        ];

        tests = _.filter(tests, t => !_.isUndefined(t));

        const obj = {
            id: obs.__id,
            tests: tests,
        };

        createECDSData(tests);

        setClinicalObs(prevState => [
            ...prevState,
            obj,
        ]);
    };

    // Transform data for ECDS data format
    const createECDSData = (obs) => {
        let arr = [];
        if (!_.isEmpty(obs)) {
            _.forEach(obs, ecds => {
                const map = _.find(ecdsMapObservations, {obs: ecds.field});
                const dict = _.find(dictionary, {ecdsVersion: version, snomedCode: map?.ecds});

                if (dict) {
                    arr.push({
                        CodedObservation_SnomedCt: dict?.snomedCode,
                        ObservationValue: ecds?.value,
                        UCUMunitOfMeasurement: ecds?.unit,
                        CodedObservationTimeStamp: moment(ecds.timestamp).utc().format(config.ecdsDateTimeFormat),
                    });
                }
            });
        }
        setEcds(prevState => [
            ...prevState,
            ...arr,
        ]);
    };

    // Find assessments for table
    const findAssessments = (id) => {
        let arr = [];
        if (!_.isEmpty(clinicalObs)) {
            arr = _.find(clinicalObs, {id: id})?.tests;
        }
        return arr || [];
    };

    const render = () => {
        if (!_.isEmpty(observation)) {
            return (
                <Grid container direction="row">
                    <Grid item xs={12} style={{marginTop: 1}}>
                        <TableContainer className={getTableScrollbar()}>
                            <Table>
                                <EcdsTableHeader/>

                                <TableBody>
                                    {observation.map((obs, index) => (
                                        <EcdsObsRow
                                            observation
                                            index={index}
                                            key={obs.__id}
                                            obs={{
                                                id: obs.__id,
                                                date: obs.examinationEnd ? moment(obs.examinationEnd).format(config.defaultDateTimeFormat) : '-',
                                                by: obs.completedByName ? obs.completedByName : '-',
                                                status: obs.status ? _.startCase(obs.status) : '-',
                                            }}
                                            innerText="Clinical Observation"
                                            innerColumns={['Name', 'Value', 'UCUM Unit', 'Timestamp']}
                                            innerRow={findAssessments(obs.__id)}
                                            editObs={props.editObs}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            );
        } else {
            return (
                <InformationBox type="info">
                    {t('Observations have not been made.')}
                </InformationBox>
            );
        }
    };

    if (props.accordion !== 'Clinical Observations') return null;

    return (
        <div className={classes.root}>
            {render()}
        </div>
    );
};

export default CodedClinicalObservations;
